/**
 * Auto Backup Manager
 * Automatic backup system that downloads JSON files to user's device
 * NO DATA SENT TO SERVER - Everything stays on user's device
 *
 * @version 1.0.0
 * @date 2025-11-13
 */

import dataManager from './DataManager';
import shadowBackup from './shadowBackup';
import { getAtlas, recomputeAtlas } from './atlasManager';
import { optimizeSourceToDataUrl } from './imageOptimizer';

class AutoBackupManager {
  constructor() {
    this.BACKUP_INTERVAL_DAYS = 7;
    this.BACKUP_KEY = 'last_backup_date';
    this.BACKUP_DISABLED_KEY = 'backup_disabled';
    this.REMIND_LATER_KEY = 'backup_remind_later';
    // Cloud nudge while travelling
    this.CLOUD_NUDGE_LAST_KEY = 'cloud_nudge_last';
    this.CLOUD_NUDGE_DISABLED_KEY = 'cloud_nudge_disabled';
    this.CLOUD_NUDGE_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 1 per day max
  }

  // ── Backup recency + cloud nudge ───────────────────────────────────────────

  /** Stamp "a backup was made now" — call after a successful download OR share. */
  markBackedUp() {
    try { localStorage.setItem(this.BACKUP_KEY, Date.now().toString()); } catch { /* ignore */ }
  }

  /** True if any of the owner's trips changed after the last backup (so there's
   *  something worth saving). Cheap: reads trip metadata, not photos. */
  async hasUnsavedChanges() {
    try {
      const trips = await dataManager.getAllTrips();
      if (!trips.length) return false;
      const last = parseInt(localStorage.getItem(this.BACKUP_KEY) || '0', 10);
      if (!last) return true; // never backed up but has trips
      const newest = trips.reduce((m, t) => {
        const ts = new Date(t.lastModified || t.updatedAt || t.createdAt || 0).getTime();
        return ts > m ? ts : m;
      }, 0);
      return newest > last;
    } catch { return false; }
  }

  isCloudNudgeDisabled() {
    return localStorage.getItem(this.CLOUD_NUDGE_DISABLED_KEY) === 'true';
  }
  disableCloudNudge() {
    try { localStorage.setItem(this.CLOUD_NUDGE_DISABLED_KEY, 'true'); } catch { /* ignore */ }
  }
  /** Mark "asked now" so the 24h cooldown applies (used by "Not now"). */
  snoozeCloudNudge() {
    try { localStorage.setItem(this.CLOUD_NUDGE_LAST_KEY, Date.now().toString()); } catch { /* ignore */ }
  }

  /** Should we surface the cloud nudge? Respects disable + 24h cooldown and only
   *  fires when there's genuinely new content to save. */
  async shouldNudgeCloud() {
    if (this.isCloudNudgeDisabled()) return false;
    const lastNudge = parseInt(localStorage.getItem(this.CLOUD_NUDGE_LAST_KEY) || '0', 10);
    if (lastNudge && (Date.now() - lastNudge) < this.CLOUD_NUDGE_COOLDOWN_MS) return false;
    return await this.hasUnsavedChanges();
  }

  /**
   * Check if backup is needed
   * @returns {boolean} True if backup should be prompted
   */
  shouldBackup() {
    // Check if user disabled backups
    if (localStorage.getItem(this.BACKUP_DISABLED_KEY) === 'true') {
      return false;
    }

    // Check if user postponed backup
    const remindLater = localStorage.getItem(this.REMIND_LATER_KEY);
    if (remindLater && Date.now() < parseInt(remindLater)) {
      return false;
    }

    // Check last backup date
    const lastBackup = localStorage.getItem(this.BACKUP_KEY);
    if (!lastBackup) return true;

    const daysSince = (Date.now() - parseInt(lastBackup)) / (1000 * 60 * 60 * 24);
    return daysSince >= this.BACKUP_INTERVAL_DAYS;
  }

  /**
   * Get days since last backup
   * @returns {number} Days since last backup
   */
  getDaysSinceLastBackup() {
    const lastBackup = localStorage.getItem(this.BACKUP_KEY);
    if (!lastBackup) return 999;

    return Math.floor((Date.now() - parseInt(lastBackup)) / (1000 * 60 * 60 * 24));
  }

  /**
   * Get last backup date as formatted string
   * @returns {string|null} Formatted date or null
   */
  getLastBackupDate() {
    const lastBackup = localStorage.getItem(this.BACKUP_KEY);
    if (!lastBackup) return null;

    const date = new Date(parseInt(lastBackup));
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Create complete backup of all trips
   * @returns {Promise<Object>} Backup object
   */
  async createBackup(onProgress = () => {}) {
    console.log('Creating backup...');

    const trips = await dataManager.getAllTrips();
    const userSettings = await dataManager.getUserSettings();

    const backup = {
      version: '2.0',
      app: 'WAYFARE x LOS DIAS',
      exportDate: new Date().toISOString(),
      exportDateFormatted: new Date().toLocaleString('es-ES'),
      exportType: 'auto_backup',
      userSettings: {
        userName: userSettings.userName || 'TRAVELER',
        language: userSettings.language || 'en',
        theme: userSettings.theme || 'auto'
        // NO sensitive data (no auth tokens, no emails, no pins)
      },
      trips: [],
      stats: {
        totalTrips: trips.length,
        activeTrips: trips.filter(t => t.status === 'active').length,
        completedTrips: trips.filter(t => t.status === 'completed').length
      }
    };

    // First pass: load each trip's photos + budget once, and count photos so the
    // progress can show current/total. (We hold the blobs we're about to encode.)
    const perTrip = [];
    let totalPhotos = 0;
    let totalNotes = 0;
    for (const trip of trips) {
      const photos = await dataManager.getPhotos(trip.tripId);
      const budget = await dataManager.getBudget(trip.tripId);
      perTrip.push({ trip, photos, budget });
      totalPhotos += photos.length;
      totalNotes += (trip.notes?.length || 0);
    }

    // Second pass: encode photos, reporting progress against the known total and
    // yielding so the UI stays responsive on big galleries.
    onProgress({ phase: 'Procesando fotos…', current: 0, total: totalPhotos });
    let done = 0;
    for (const { trip, photos, budget } of perTrip) {
      const exportedPhotos = await this._exportPhotos(photos, () => {
        done++;
        onProgress({ current: done, total: totalPhotos });
      });

      backup.trips.push({
        tripId: trip.tripId,
        destination: trip.destination,
        dates: trip.dates,
        status: trip.status,
        stats: trip.stats,
        notes: trip.notes || [],
        reflections: trip.reflections || [],
        challenges: trip.challenges || [],
        luggage: trip.luggage || [],
        groceries: trip.groceries || [],
        gifts: trip.gifts || [],
        lodgings: trip.lodgings || [],
        privateDocuments: trip.privateDocuments || [],
        travelInfo: trip.travelInfo || {},
        revealedPois: trip.revealedPois || [],
        surfacedPois: trip.surfacedPois || [],
        dayClosures: trip.dayClosures || {},
        sealedAt: trip.sealedAt || null,
        openThreads: trip.openThreads || [],
        budget: budget,
        photos: exportedPhotos,
        createdAt: trip.createdAt,
        lastModified: trip.lastModified
      });
    }

    backup.stats.totalPhotos = totalPhotos;
    backup.stats.totalNotes = totalNotes;

    // Sprint 2 — include the derived atlas (reconstructible; restore recomputes anyway)
    backup.atlas = await getAtlas().catch(() => null);

    console.log('Backup created:', backup.stats);
    return backup;
  }

  /**
   * Export photos as base64 for JSON backup
   * @private
   * @param {Array} photos - Photos from IndexedDB
   * @returns {Promise<Array>} Photos as base64
   */
  async _exportPhotos(photos, onPhoto = () => {}) {
    const exported = [];

    let i = 0;
    for (const photo of photos) {
      try {
        // Optimize on the way out (safety net for legacy full-size photos so the
        // backup stays shareable). Already-small photos pass through untouched.
        const base64 = await optimizeSourceToDataUrl(photo.data);

        exported.push({
          id: photo.id,
          tripId: photo.tripId,
          data: base64,
          caption: photo.caption || '',
          location: photo.location || '',
          timestamp: photo.timestamp,
          size: photo.size || 0
        });
      } catch (error) {
        console.error('Error exporting photo:', photo.id, error);
        // Continue with other photos
      }
      onPhoto();
      // Yield to the UI every few photos so a big gallery doesn't freeze it.
      if ((++i % 5) === 0) await new Promise(r => setTimeout(r, 0));
    }

    return exported;
  }

  /**
   * Convert Blob to base64
   * @private
   */
  async _blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Convert base64 to Blob
   * @private
   */
  async _base64ToBlob(base64) {
    const response = await fetch(base64);
    return response.blob();
  }

  // Same as createBackup() but skips photos — much smaller, ideal for Web Share
  async createBackupWithoutPhotos() {
    const trips = await dataManager.getAllTrips();
    const userSettings = await dataManager.getUserSettings();
    const date = new Date().toISOString();

    const backup = {
      version: '2.0',
      app: 'WAYFARE x LOS DIAS',
      exportDate: date,
      exportDateFormatted: new Date().toLocaleString('es-ES'),
      exportType: 'auto_backup_no_photos',
      userSettings: {
        userName: userSettings.userName || 'TRAVELER',
        language: userSettings.language || 'en',
        theme: userSettings.theme || 'auto',
      },
      trips: [],
      stats: { totalTrips: trips.length, totalPhotos: 0, totalNotes: 0 },
    };

    for (const trip of trips) {
      const budget = await dataManager.getBudget(trip.tripId);
      backup.stats.totalNotes += (trip.notes?.length || 0);
      backup.trips.push({
        tripId: trip.tripId,
        destination: trip.destination,
        dates: trip.dates,
        status: trip.status,
        stats: trip.stats,
        notes: trip.notes || [],
        reflections: trip.reflections || [],
        challenges: trip.challenges || [],
        luggage: trip.luggage || [],
        groceries: trip.groceries || [],
        gifts: trip.gifts || [],
        lodgings: trip.lodgings || [],
        privateDocuments: trip.privateDocuments || [],
        travelInfo: trip.travelInfo || {},
        revealedPois: trip.revealedPois || [],
        surfacedPois: trip.surfacedPois || [],
        dayClosures: trip.dayClosures || {},
        sealedAt: trip.sealedAt || null,
        openThreads: trip.openThreads || [],
        budget,
        photos: [], // intentionally empty
        createdAt: trip.createdAt,
        lastModified: trip.lastModified,
      });
    }

    backup.atlas = await getAtlas().catch(() => null);
    return backup;
  }

  // Build a File object ready for Web Share API (uses octet-stream for max compat)
  backupToShareFile(backup, suffix = '') {
    const json  = JSON.stringify(backup, null, 2);
    const date  = new Date().toISOString().split('T')[0];
    const name  = `wayfare-backup-${date}${suffix}.json`;
    return new File([json], name, { type: 'application/octet-stream' });
  }

  /**
   * Download backup file to user's device
   * @param {Object} backup - Backup object
   * @returns {Promise<string>} Downloaded filename
   */
  async downloadBackup(backup) {
    const json = JSON.stringify(backup, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const filename = `wayfare-backup-${new Date().toISOString().split('T')[0]}.json`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);

    // Update last backup date
    localStorage.setItem(this.BACKUP_KEY, Date.now().toString());

    console.log('Backup downloaded:', filename);
    return filename;
  }

  /**
   * Parse any supported backup format from raw JSON text.
   * Returns { trips, meta } or throws with a user-readable message.
   * @private
   */
  _parseRawBackup(text) {
    let raw;
    try {
      raw = JSON.parse(text);
    } catch (e) {
      throw new Error('El archivo no es un JSON válido. Puede estar dañado o ser el archivo incorrecto.');
    }

    let trips = null;
    let meta = {};

    // Format 1: { trips: [...] } — standard full backup (v2.0) and shadow backup
    if (raw && Array.isArray(raw.trips)) {
      trips = raw.trips;
      meta = { version: raw.version || 'legacy', exportDate: raw.exportDate, userSettings: raw.userSettings };
    }
    // Format 2: { trip: {...} } — single-trip export from DataManager.exportTrip()
    else if (raw && raw.trip && typeof raw.trip === 'object' && !Array.isArray(raw.trip)) {
      const t = { ...raw.trip };
      if (raw.budget) t.budget = raw.budget;
      trips = [t];
      meta = { version: raw.version || 'legacy', exportDate: raw.exportDate };
    }
    // Format 3: bare array at root
    else if (Array.isArray(raw)) {
      trips = raw;
      meta = { version: 'legacy' };
    }
    // Format 4: wayfare-trip file — { format:"wayfare-trip", tripId, destination, data:{notes,budget,...} }
    // This is the file the app creates when the user starts a new trip
    else if (raw && raw.tripId && raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
      const t = {
        tripId: raw.tripId,
        destination: raw.destination,
        dates: raw.dates,
        status: raw.status || 'active',
        notes: raw.data.notes || [],
        reflections: raw.data.reflections || [],
        challenges: raw.data.challenges || [],
        luggage: raw.data.luggage || [],
        groceries: raw.data.groceries || [],
        gifts: raw.data.gifts || [],
        lodgings: raw.data.lodgings || [],
        privateDocuments: raw.data.privateDocuments || [],
        travelInfo: raw.data.travelInfo || {},
        revealedPois: raw.data.revealedPois || [],
        surfacedPois: raw.data.surfacedPois || [],
        dayClosures: raw.data.dayClosures || {},
        sealedAt: raw.data.sealedAt || null,
        openThreads: raw.data.openThreads || [],
        budget: raw.data.budget || null,
        photos: raw.data.photos || [],
        createdAt: raw.createdAt,
        lastModified: raw.lastUpdated || raw.lastModified,
      };
      trips = [t];
      meta = { version: raw.version || 'legacy', exportDate: raw.lastUpdated || raw.createdAt };
    }
    // Format 5: { data: { trips: [...] } } or { data: [...] }
    else if (raw && raw.data) {
      if (Array.isArray(raw.data.trips)) {
        trips = raw.data.trips;
        meta = { version: raw.version || 'legacy', exportDate: raw.exportDate };
      } else if (Array.isArray(raw.data)) {
        trips = raw.data;
        meta = { version: raw.version || 'legacy' };
      }
    }
    // Format 6: versionManager internal snapshot — { tripData: {...}, userSettings: {...} }
    else if (raw && raw.tripData && typeof raw.tripData === 'object') {
      trips = [raw.tripData];
      meta = { version: raw.version || 'legacy', userSettings: raw.userSettings };
    }
    // Format 7: last-ditch — find any top-level array of objects that look like trips
    else if (raw && typeof raw === 'object') {
      const candidate = Object.values(raw).find(
        v => Array.isArray(v) && v.length > 0 && v[0] && typeof v[0] === 'object'
      );
      if (candidate) {
        trips = candidate;
        meta = { version: 'legacy' };
      }
    }

    if (!trips) {
      throw new Error(
        'No se encontraron viajes en este archivo.\n' +
        'Formatos aceptados: backup completo, exportación de un viaje, array de viajes.'
      );
    }

    // Keep only objects that look like trips
    const valid = trips.filter(
      t => t && typeof t === 'object' && !Array.isArray(t) &&
           (t.destination || t.tripId || t.dates)
    );

    if (valid.length === 0) {
      throw new Error(
        `Se encontró una lista pero ningún objeto parece ser un viaje válido.\n` +
        `Asegúrate de seleccionar el archivo de backup correcto.`
      );
    }

    return { trips: valid, meta };
  }

  /**
   * Analyse a backup file and return a preview without restoring anything.
   * Use this to show the user what's in the file before committing.
   * @param {File} file
   * @returns {Promise<{ trips, meta, tripCount, tripNames, exportDate, version }>}
   */
  async analyzeBackupFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const { trips, meta } = this._parseRawBackup(e.target.result);
          const hasData = trips.some(t =>
            (t.notes?.length > 0) || (t.reflections?.length > 0) ||
            (t.challenges?.length > 0) || (t.luggage?.length > 0) ||
            (t.lodgings?.length > 0) || (t.photos?.length > 0)
          );
          resolve({
            trips,
            meta,
            tripCount: trips.length,
            tripNames: trips.slice(0, 5).map(t => t.destination?.city || t.tripId || 'Viaje'),
            exportDate: meta.exportDate || null,
            version: meta.version || 'legacy',
            hasData,
          });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsText(file);
    });
  }

  /**
   * Restore trips from the shadow backup stored in localStorage.
   * Photos will be missing (they were in IndexedDB), but all metadata is restored.
   * @returns {Promise<Object>} Restore result
   */
  async restoreFromShadow() {
    const shadow = shadowBackup.load();
    if (!shadow || !Array.isArray(shadow.trips) || shadow.trips.length === 0) {
      throw new Error('No hay copia de seguridad en memoria del navegador');
    }

    const currentTrips = await dataManager.getAllTrips();
    let restored = 0;
    const errors = [];

    for (const tripData of shadow.trips) {
      try {
        await this._restoreTrip(tripData);
        restored++;
      } catch (err) {
        errors.push({
          tripId: tripData.tripId,
          destination: tripData.destination?.city || 'Unknown',
          error: err.message,
        });
      }
    }

    // Atlas is derived — rebuild it from the restored trips (source of truth).
    try { await recomputeAtlas(); } catch { /* cache */ }

    return {
      success: true,
      tripsRestored: restored,
      tripsFailed: errors.length,
      previousTrips: currentTrips.length,
      backupDate: new Date(shadow.savedAt).toISOString(),
      photosIncluded: false,
      errors,
    };
  }

  /**
   * Restore from backup file
   * @param {File} backupFile - JSON backup file
   * @returns {Promise<Object>} Restore result
   */
  async restoreFromBackup(backupFile) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const { trips: tripsData, meta } = this._parseRawBackup(e.target.result);
          const backup = { ...meta, trips: tripsData };

          console.log('Restoring backup:', {
            version: backup.version,
            trips: backup.trips.length,
            date: backup.exportDate,
          });

          // Get current trips for confirmation message
          const currentTrips = await dataManager.getAllTrips();

          // Restore trips
          let restored = 0;
          const errors = [];

          for (const tripData of backup.trips) {
            try {
              await this._restoreTrip(tripData);
              restored++;
            } catch (error) {
              console.error('Error restoring trip:', tripData.tripId, error);
              errors.push({
                tripId: tripData.tripId,
                destination: tripData.destination?.city || 'Unknown',
                error: error.message
              });
            }
          }

          // Restore user settings
          if (backup.userSettings) {
            const currentSettings = await dataManager.getUserSettings();
            await dataManager.saveUserSettings({
              ...currentSettings,
              userName: backup.userSettings.userName,
              theme: backup.userSettings.theme,
              language: backup.userSettings.language
            });
          }

          // Atlas is derived — rebuild it from the restored trips.
          try { await recomputeAtlas(); } catch { /* cache */ }

          resolve({
            success: true,
            tripsRestored: restored,
            tripsFailed: errors.length,
            previousTrips: currentTrips.length,
            backupDate: backup.exportDate,
            errors
          });

        } catch (error) {
          console.error('Restore error:', error);
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Error al leer el archivo de backup'));
      reader.readAsText(backupFile);
    });
  }

  /**
   * Restore single trip from backup
   * @private
   */
  async _restoreTrip(tripData) {
    const existingTrip = await dataManager.getTrip(tripData.tripId);
    if (existingTrip) {
      // Merge strategy: never overwrite existing non-empty data with an empty array.
      // This prevents a trip-creation file (all empty arrays) from wiping live notes/activities.
      const keep = (existing, incoming) => {
        if (!Array.isArray(incoming)) return incoming ?? existing;
        return (incoming.length === 0 && Array.isArray(existing) && existing.length > 0)
          ? existing : incoming;
      };
      const keepObj = (existing, incoming) =>
        (incoming && Object.keys(incoming).length > 0) ? incoming : (existing || incoming);

      console.log('Trip already exists, merging:', tripData.tripId);
      await dataManager.updateTrip(tripData.tripId, {
        destination: tripData.destination || existingTrip.destination,
        dates: tripData.dates || existingTrip.dates,
        status: tripData.status || existingTrip.status,
        notes:            keep(existingTrip.notes,            tripData.notes),
        reflections:      keep(existingTrip.reflections,      tripData.reflections),
        challenges:       keep(existingTrip.challenges,       tripData.challenges),
        luggage:          keep(existingTrip.luggage,          tripData.luggage),
        groceries:        keep(existingTrip.groceries,        tripData.groceries),
        gifts:            keep(existingTrip.gifts,            tripData.gifts),
        lodgings:         keep(existingTrip.lodgings,         tripData.lodgings),
        privateDocuments: keep(existingTrip.privateDocuments, tripData.privateDocuments),
        travelInfo:       keepObj(existingTrip.travelInfo,    tripData.travelInfo),
        revealedPois:     keep(existingTrip.revealedPois,     tripData.revealedPois),
        surfacedPois:     keep(existingTrip.surfacedPois,     tripData.surfacedPois),
        dayClosures:      { ...(existingTrip.dayClosures || {}), ...(tripData.dayClosures || {}) },
        // Sprint 2 — prefer an existing seal/threads; never clobber with empty
        sealedAt:         tripData.sealedAt || existingTrip.sealedAt || null,
        openThreads:      keep(existingTrip.openThreads,      tripData.openThreads),
      });
    } else {
      // Create new trip, preserving the original tripId so photos/budget stay linked
      await dataManager.createTrip({
        tripId: tripData.tripId,
        destination: tripData.destination,
        dates: tripData.dates,
        status: tripData.status,
        notes: tripData.notes,
        reflections: tripData.reflections,
        challenges: tripData.challenges,
        luggage: tripData.luggage,
        groceries: tripData.groceries,
        gifts: tripData.gifts,
        lodgings: tripData.lodgings,
        privateDocuments: tripData.privateDocuments,
        travelInfo: tripData.travelInfo,
        revealedPois: tripData.revealedPois || [],
        surfacedPois: tripData.surfacedPois || [],
        dayClosures: tripData.dayClosures || {},
        sealedAt: tripData.sealedAt || null,
        openThreads: tripData.openThreads || []
      }, false);
    }

    // Restore photos
    if (tripData.photos && tripData.photos.length > 0) {
      for (const photo of tripData.photos) {
        try {
          const blob = await this._base64ToBlob(photo.data);
          await dataManager.addPhoto(tripData.tripId, {
            data: blob,
            caption: photo.caption,
            location: photo.location
          });
        } catch (error) {
          console.error('Error restoring photo:', photo.id, error);
          // Continue with other photos
        }
      }
    }

    // Restore budget
    if (tripData.budget) {
      await dataManager.saveBudget(tripData.tripId, tripData.budget);
    }
  }

  /**
   * Postpone backup reminder
   * @param {number} days - Days to postpone
   */
  postponeBackup(days = 3) {
    const remindAt = Date.now() + (days * 24 * 60 * 60 * 1000);
    localStorage.setItem(this.REMIND_LATER_KEY, remindAt.toString());
  }

  /**
   * Disable backup prompts permanently
   */
  disableBackups() {
    localStorage.setItem(this.BACKUP_DISABLED_KEY, 'true');
  }

  /**
   * Enable backup prompts
   */
  enableBackups() {
    localStorage.removeItem(this.BACKUP_DISABLED_KEY);
  }

  /**
   * Check if backups are disabled
   * @returns {boolean}
   */
  areBackupsDisabled() {
    return localStorage.getItem(this.BACKUP_DISABLED_KEY) === 'true';
  }

  /**
   * Get backup status for UI
   * @returns {Object} Backup status
   */
  getBackupStatus() {
    return {
      needed: this.shouldBackup(),
      disabled: this.areBackupsDisabled(),
      daysSinceLastBackup: this.getDaysSinceLastBackup(),
      lastBackupDate: this.getLastBackupDate()
    };
  }
}

// Export singleton instance
const autoBackupManager = new AutoBackupManager();
export default autoBackupManager;
